import { ConfigurablePropsFromDescription } from '../../../helpers/widget'
import PublishUtils from '../../../helpers/PublishUtils'
import { useRequiredContext } from '../../../utils/use-required-context'
import { createContext } from 'react'
import { IconHelp } from '../IconHelp'
import { AfterEventActionsConfig, TreeButtonsConfig } from "./config-schema"
import { GeneralWidgetConfig } from "../../WidgetConfiguration"
import {
  BUTTON_EXAMPLE, CreateExampleConfigHelp,
  REORDER_ACTION_EXAMPLE, REPARENT_ACTION_EXAMPLE,
} from "./example-json-configs"
import { Override } from "../../../utils/typescript"

export const configOptions = [
  {
    name: 'query',
    type: 'yasgui',
    label: '?uri ?label ?color ?parentUri ?icon ?iconColor ?iconSize ?tooltip ?defaultChecked(xsd:boolean) ?rightLabel ?rightLabelColor ?rightIcon ?rightIconColor ?rightIconSize ?canDropInto',
    helpComponent: TreePlusPlusHelp,
    defaultQuery: `
PREFIX e: <https://example-tree.org#>
PREFIX rdfs: <http://www.w3.org/2000/01/rdf-schema#>

SELECT * WHERE {
    GRAPH <https://example-tree.org> {
        ?uri a e:Node ;
            rdfs:label ?label .
        OPTIONAL {
            ?uri e:parentUri ?parentUri .
        }
        OPTIONAL {
          ?uri e:orderNr ?order .
        }
    }
}
ORDER BY ASC(?order)
`
  },
  {
    name: 'selectMode',
    type: 'select',
    label: 'Tree selection mode',
    options: [
      { label: 'Disabled', value: 'disabled' },
      { label: 'Single', value: 'single' },
      { label: 'Multiple (use Ctrl and Shift)', value: 'multiple' }
    ],
  },
  {
    name: 'checkboxesEnabled',
    type: 'boolean',
    label: 'Enable checkboxes (this is separate from selection)',
  },
  {
    name: 'checkboxLayout',
    type: 'select',
    label: 'Checkboxes presentation (aligned or nested)',
    options: [
      { label: 'Nested (recommended)', value: 'nested' },
      { label: 'Aligned (deprecated, not usable with drag-and-drop)', value: 'aligned' },
    ],
  },
  {
    name: 'variant',
    type: 'select',
    label: 'Visual spacing variant.',
    inputVariant: 'radiogroup',
    options: [
      { label: 'Default', value: 'default' },
      { label: 'Medium', value: 'medium' },
      { label: 'Compact', value: 'compact'}
    ]
  },
  {
    name: 'disableDeselection',
    type: 'boolean',
    label: 'Disable deselection',
  },
  {
    name: 'lastSelectedItemTopic',
    type: 'text',
    label: 'Topic to publish last selected item to'
  },
  {
    name: 'selectedItemsTopic',
    type: 'text',
    label: 'Topic to publish selected items to'
  },
  {
    name: 'checkedItemsTopic',
    type: 'text',
    label: 'Topic to publish checked items to'
  },
  {
    name: 'checkedItemsPublishMode',
    type: 'select',
    label: 'How to publish checked items',
    options: [
      { label: 'All nodes', value: 'all' },
      { label: 'Leaf nodes only', value: 'leaves' },
    ],
  },
  {
    name: 'selectedItemSubscription',
    type: 'text',
    label: 'Listen topic for item selection'
  },
  {
    name: 'selectedItemsSubscription',
    type: 'text',
    label: 'Listen topic for multi item selection. This is based on your multiple value configuration.'
  },
  {
    name: 'checkedItemsSubscription',
    type: 'text',
    label: 'Listen topic for checking items. This is based on your multiple value configuration.'
  },
  {
    name: 'selectedItemRevealMode',
    type: 'select',
    label: 'Behaviour for automatically revealing selected item. Only applies to the single item selection topic.',
    defaultValue: 'disabled',
    options: [
      { label: 'Do not reveal', value: 'disabled' },
      { label: 'Reveal instantly', value: 'instant' },
      { label: 'Reveal smoothly', value: 'smooth' },
    ],
  },
    {
    name: 'disableAutoItemSelectionAfterCreation',
    type: 'boolean',
    label: 'Disable auto-selection of item after its creation',
  },
  {
    name: 'editTriplesTemplate',
    type: 'turtle',
    label: 'EXPERIMENTAL: TriG for built-in edit functionality. Available extra variables: {{node_uri}} {{node_label}} {{node_parentUri}}, optional: {{node_index}}',
    defaultQuery: `
@prefix e:    <https://example-tree.org#> .
@prefix rdfs: <http://www.w3.org/2000/01/rdf-schema#> .
@prefix xsd:  <http://www.w3.org/2001/XMLSchema#> .

<https://example-tree.org> {
  <{{node_uri}}> a e:Node ;
      rdfs:label "{{node_label}}" ;
      e:parentUri <{{node_parentUri}}> ;
      e:orderNr "{{node_index}}"^^xsd:integer .
}`
  },
  {
    formGroup: 'Changelog configuration',
    name: 'mutationLogsEnabled',
    type: 'boolean',
    label: 'enable edit-logs to triples',

  },
  {
    formGroup: 'Changelog configuration',
    name: 'mutationLogsGraph',
    type: 'text',
    label: 'graph to write edit-logs in',
    topicValueInterpolationEnabled: true,
  },
  {
    formGroup: 'Changelog configuration',
    name: 'mutationLogsCustomStatements',
    type: 'text',
    label: 'custom statements for edit-logs',
    topicValueInterpolationEnabled: true,
  },
  {
    name: 'buttonsConfig',
    type: 'json-text',
    label: 'EXPERIMENTAL: Tool buttons JSON configuration. See help for an example.',
    helpComponent: CreateExampleConfigHelp(BUTTON_EXAMPLE)
  },
  {
    name: 'uriModificationEnabled',
    type: 'boolean',
    label: 'Allow URI to be modified. If this is disabled, then only the label can be modified.'
  },
  {
    name: 'defaultUriTemplate',
    type: 'text',
    label: 'New default URI template for editing functionality. Use {{generated_uuid}} for random UUID'
  },
  {
    name: 'dndEnabled',
    type: 'boolean',
    label: 'EXPERIMENTAL: Enable drag-and-drop (master switch)',
  },
  {
    name: 'dndDragActivationMode',
    type: 'select',
    label: 'Drag-and-drop activation mode',
    options: [
      { label: 'Via drag handle', value: 'handle' },
      { label: 'Via entire row',  value: 'row' }
    ],
  },
  {
    name: 'dndReparentEnabled',
    type: 'boolean',
    label: 'Enable reparenting functionality via drag-and-drop. You can allow/disallow nodes from being a valid drop target with the ?canDropInto query parameter.',
  },
  {
    name: 'dndReorderEnabled',
    type: 'boolean',
    label: `Enable reordering functionality via drag-and-drop.
    Your edit template must include a quad for {{node_index}}, representing the node's position with an integral, starting from 0.
    SPARQL query should use ORDER BY ASC on this index to display with the correct order.`,
  },
  {
    name: 'onReparentSuccessConfig',
    type: 'json-text',
    label: 'Actions after drag & drop reparent success. See help for an example.',
    helpComponent: CreateExampleConfigHelp(REPARENT_ACTION_EXAMPLE)
  },
  {
    name: 'onReorderSuccessConfig',
    type: 'json-text',
    label: 'Actions after drag & drop reorder success. See help for an example.',
    helpComponent: CreateExampleConfigHelp(REORDER_ACTION_EXAMPLE)
  },
  PublishUtils.getTimestampParametersDef(),
  PublishUtils.getMultipleValueFormatOptions()
] as const

export type RawTreePlusPlusConfigOptions = ConfigurablePropsFromDescription<typeof configOptions> & GeneralWidgetConfig
export type TreePlusPlusConfigOptions = Override<RawTreePlusPlusConfigOptions, {
  buttonsConfig?: TreeButtonsConfig | null,
  onReorderSuccessConfig?: AfterEventActionsConfig | null,
  onReparentSuccessConfig?: AfterEventActionsConfig | null,
  sparqlEndpoint: string,
  _parsingErrors: Error[]
}>

export const TreeConfigContext = createContext<TreePlusPlusConfigOptions | null>(null)

export const useTreeConfig = () => useRequiredContext(TreeConfigContext)

function TreePlusPlusHelp() {
  type HelpParam = { name: string, type: string, description?: string }
  const PARAMS: HelpParam[] = [
    { name: '?uri', type: 'URI or literal' },
    { name: '?parentUri', type: 'URI or literal' },
    { name: '?label', type: 'literal' },
    { name: '?color', type: 'literal', description: 'css color' },
    { name: '?icon', type: 'literal' },
    { name: '?iconColor', type: 'literal', description: 'css color' },
    { name: '?iconSize', type: 'literal', description: 'css size unit' },
    { name: '?defaultChecked', type: 'xsd:boolean literal' },
    { name: '?rightLabel', type: 'literal', description: 'optional label at the right size' },
    { name: '?rightLabelColor', type: 'literal', description: 'css color (default is grey)' },
    { name: '?rightIcon', type: 'literal', description: 'default is radix-dot' },
    { name: '?rightIconColor', type: 'literal', description: 'css color' },
    { name: '?rightIconSize', type: 'literal', description: 'css size unit' },
    { name: '?canDropInto', type: 'literal', description: 'xsd:boolean literal, whether a valid drag-and-drop target' },
  ] as const
  return (
    <div>
      <table>
        <tbody>
        {PARAMS.map((param, i) => (
          <tr>
            <td>{param.name}</td>
            <td>{param.type}</td>
            <td>{param.description ?? ''}</td>
          </tr>
        ))}
        </tbody>
      </table>

      <p>If using drag-and-drop reorder functionality, the results must be ordered (ascending) by the predicate defining the node's order.</p>

      <IconHelp className="mt-3" />
    </div>

  )
}
